import { ActionIcon, Box, Group, Select, Stack, Text, TextInput, rem, useMantineColorScheme } from "@mantine/core";
import { ReportVariable } from "@bi-studio/shared/lib";
import { Trash } from "lucide-react";
import { SqlEditor } from "@/components/common/SqlEditor";

interface VariableConfigProps {
    variable: ReportVariable;
    onChange: (variable: ReportVariable) => void;
    onRemove: () => void;
}

export function VariableConfig({ variable, onChange, onRemove }: VariableConfigProps) {
    const { colorScheme } = useMantineColorScheme();

    return (
        <Box
            p="md"
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                backdropFilter: 'blur(5px)',
            }}
        >
            <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm">{variable.name}</Text>
                <ActionIcon color="red" variant="subtle" onClick={onRemove} size="sm">
                    <Trash size={16} />
                </ActionIcon>
            </Group>

            <Stack gap="sm">
                <Group grow align="flex-start">
                    <TextInput
                        label="Label"
                        placeholder="Display Label"
                        value={variable.label || ""}
                        onChange={(e) => onChange({ ...variable, label: e.currentTarget.value })}
                        size="xs"
                    />

                    <Select
                        label="Type"
                        data={[
                            { value: "text", label: "Text Input" },
                            { value: "select", label: "Dropdown (Select)" },
                            { value: "date", label: "Date Picker" },
                        ]}
                        value={variable.type}
                        onChange={(val) => onChange({ ...variable, type: val as any })}
                        size="xs"
                    />
                </Group>

                {variable.type === "select" && (
                    <Box
                        pl="md"
                        style={{
                            borderLeft: `2px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                            marginTop: rem(4)
                        }}
                    >
                        <Select
                            label="Source"
                            data={[
                                { value: "static", label: "Static List" },
                                { value: "query", label: "SQL Query" },
                            ]}
                            value={variable.selectConfig?.source || "static"}
                            onChange={(val) =>
                                onChange({
                                    ...variable,
                                    selectConfig: {
                                        ...variable.selectConfig,
                                        source: val as "static" | "query",
                                    },
                                })
                            }
                            mb="xs"
                            size="xs"
                        />

                        {variable.selectConfig?.source === "query" ? (
                            <Box>
                                <Text size="xs" fw={500} mb={4}>
                                    Query
                                </Text>
                                <Text size="xs" c="dimmed" mb="xs">
                                    Query must return <code>value</code> and optional <code>label</code> columns.
                                </Text>
                                <Box h={200} style={{ border: '1px solid var(--modal-border)', borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}>
                                    <SqlEditor
                                        value={variable.selectConfig.query || ""}
                                        onChange={(val) =>
                                            onChange({
                                                ...variable,
                                                selectConfig: { ...variable.selectConfig, source: "query", query: val },
                                            })
                                        }
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <TextInput
                                label="Options"
                                description="Comma separated values"
                                placeholder="Option 1, Option 2, Option 3"
                                value={variable.selectConfig?.staticOptions?.join(", ") || ""}
                                onChange={(e) =>
                                    onChange({
                                        ...variable,
                                        selectConfig: {
                                            ...variable.selectConfig,
                                            source: "static",
                                            staticOptions: e.currentTarget.value.split(",").map((s) => s.trim()),
                                        },
                                    })
                                }
                                size="xs"
                            />
                        )}
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
